import { DateTime } from "luxon";
import { ParkerDTO, UpdateParkerDTO, UpdateParkerSubscriptionAssignmentDTO, UpdateParkerTagDTO } from "src/app/base/models/models.swagger.generated";

export class UpdateParkerUtil {
  public static createUpdateParkerDTO(parker: ParkerDTO): UpdateParkerDTO {
    const updateData = new UpdateParkerDTO({
      ...parker,
      tags: parker.tags?.map(tag => new UpdateParkerTagDTO(tag)),
      parkerSubscriptionAssignments: parker.parkerSubscriptionAssignments?.map(assignment => this.createUpdateParkerSubscriptionAssignmentDTO(assignment))
    });
    return updateData;
  }

  public static createUpdateParkerSubscriptionAssignmentDTO(assignment: any): UpdateParkerSubscriptionAssignmentDTO {
    return new UpdateParkerSubscriptionAssignmentDTO({
      ...assignment,
      id: assignment.id < 0 ? 0 : assignment.id,
      subscriptionId: assignment.subscription.id,
      isDefaultSubscription: assignment.isFavorite
    });
  }

  public static addOrMergeSubscriptionAssignment(updateData: UpdateParkerDTO, subscriptionAssignment: UpdateParkerSubscriptionAssignmentDTO, defaultFromTime: DateTime): void {
    let addFullSubscription = true;
    const existingSubscriptionAssignments = updateData.parkerSubscriptionAssignments?.filter(a => a.subscriptionId === subscriptionAssignment.subscriptionId).sort((a, b) => a.validFrom.toMillis() - b.validFrom.toMillis()) ?? [];
    if (existingSubscriptionAssignments.length > 0) {
      const existingSubscriptionsToKeep = [];
      existingSubscriptionAssignments.forEach(a => {
        // validFrom >= endDate
        if (subscriptionAssignment.validTo != null && a.validFrom >= subscriptionAssignment.validTo) {
          // nothing to change
          existingSubscriptionsToKeep.push(a);
          return;
        }

        // validTo <= startDate
        if (a.validTo != null && a.validTo < subscriptionAssignment.validFrom) {
          // nothing to change
          existingSubscriptionsToKeep.push(a);
          return;
        }

        // everything else is overlapping
        // inactive subscriptions are shortened or eliminated if starting in the future
        if (!a.active) {
          // inactive subscription starting in the future are eliminated even if they would continue after the new subscription
          if (a.validFrom < defaultFromTime) { // starts in the past -> cannot be deleted, so adjust end date
            a.validTo = subscriptionAssignment.validFrom;
            existingSubscriptionsToKeep.push(a);
          }
          return;
        }

        if (addFullSubscription) { // so far we are working with the new subscription
          // we will start working with this subscription assignment but first we need to adjust the start and end date
          if (a.validFrom > subscriptionAssignment.validFrom) {
            a.validFrom = subscriptionAssignment.validFrom; // adjust start date
          }
          if (subscriptionAssignment.validTo == null || ( a.validTo != null && a.validTo < subscriptionAssignment.validTo)) {
            a.validTo = subscriptionAssignment.validTo; // adjust end date
          }
          subscriptionAssignment = a;
          addFullSubscription = false;
          existingSubscriptionsToKeep.push(a);
          return;
        }

        // we are already working with an existing subscription assignment -> extend this one further, eliminate others where possible
        if (a.validTo == null || (subscriptionAssignment.validTo != null && a.validTo > subscriptionAssignment.validTo)) {
          subscriptionAssignment.validTo = a.validTo;
        }
        if (a.validFrom < defaultFromTime) {
          // we have to keep the record, but we shorten it
          a.validTo = defaultFromTime;
          existingSubscriptionsToKeep.push(a);
        }
      });
      updateData.parkerSubscriptionAssignments = updateData.parkerSubscriptionAssignments?.filter(a => a.subscriptionId !== subscriptionAssignment.subscriptionId) ?? [];
      updateData.parkerSubscriptionAssignments.push(...existingSubscriptionsToKeep);
    }

    if (addFullSubscription) {
      updateData.parkerSubscriptionAssignments.push(subscriptionAssignment);
    }
  }
}
